import React, { useState, useEffect } from 'react';
import { dispatchesAPI, machinesAPI, customersAPI } from '../api/api';
import DataTable from '../components/DataTable';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Plus, X, Save, Package, Trash2, Printer, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import PasswordModal from '../components/PasswordModal';
import { verifyAdminPassword } from '../utils/passwordUtils';
import axios from 'axios';
import { normalizeGreek } from '../utils/greekUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DispatchesPage = () => {
  const [dispatches, setDispatches] = useState([]);
  const [machines, setMachines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDispatch, setEditingDispatch] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // --- NEW CREATE flow state ---
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerMachines, setCustomerMachines] = useState([]);
  // Each entry: { machineId, machineName, consumables: [{consumable_id, name, quantity}], availableConsumables: [] }
  const [machineDispatches, setMachineDispatches] = useState([]);
  const [dispatchDate, setDispatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [dispatchNote, setDispatchNote] = useState('');
  const [notes, setNotes] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');

  // Single edit form state
  const [formData, setFormData] = useState({
    machine_id: '', consumable_id: '',
    dispatch_date: new Date().toISOString().split('T')[0],
    dispatch_note: '', quantity: 1, notes: ''
  });
  const [editConsumables, setEditConsumables] = useState([]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [dispatchesRes, machinesRes, customersRes] = await Promise.all([
        dispatchesAPI.getAll(),
        machinesAPI.getAll(),
        customersAPI.getAll()
      ]);
      setDispatches(dispatchesRes.data);
      setMachines(machinesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης');
    } finally {
      setLoading(false);
    }
  };

  const fetchMachineConsumables = async (machineId) => {
    if (!machineId) return [];
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/consumables/machine/${machineId}/consumables`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch { return []; }
  };

  // --- Customer selection ---
  const handleCustomerSelect = (customerId) => {
    const cid = Number(customerId);
    setSelectedCustomerId(cid);
    const filtered = machines.filter(m => m.customer_id === cid);
    setCustomerMachines(filtered);
    // Auto-create first empty machine block
    if (filtered.length > 0) {
      setMachineDispatches([{ machineId: null, machineName: '', consumables: [], availableConsumables: [] }]);
    } else {
      setMachineDispatches([]);
    }
  };

  // --- Add empty machine block (user picks machine inside the block) ---
  const handleAddMachineBlock = () => {
    setMachineDispatches(prev => [...prev, {
      machineId: null,
      machineName: '',
      consumables: [],
      availableConsumables: []
    }]);
  };

  // --- Select machine inside a block ---
  const handleSelectMachineInBlock = async (machineIdx, machineId) => {
    if (!machineId) return;
    const mid = Number(machineId);
    if (machineDispatches.find((md, i) => i !== machineIdx && md.machineId === mid)) {
      toast.error('Αυτό το μηχάνημα υπάρχει ήδη στη λίστα');
      return;
    }
    const machine = customerMachines.find(m => m.id === mid);
    if (!machine) return;
    const consumables = await fetchMachineConsumables(mid);
    const updated = [...machineDispatches];
    updated[machineIdx] = {
      machineId: mid,
      machineName: `${machine.brand} ${machine.model} (${machine.machine_serial})`,
      consumables: [],
      availableConsumables: consumables
    };
    setMachineDispatches(updated);
  };

  // --- Add consumable to a machine block ---
  const addConsumableToMachine = (machineIdx, consumableId) => {
    if (!consumableId) return;
    const updated = [...machineDispatches];
    const block = updated[machineIdx];
    const cons = block.availableConsumables.find(c => c.id === Number(consumableId));
    if (!cons) return;
    if (block.consumables.find(c => c.consumable_id === cons.id)) {
      toast.error('Υπάρχει ήδη');
      return;
    }
    block.consumables.push({ consumable_id: cons.id, name: cons.name, quantity: 1 });
    setMachineDispatches(updated);
  };

  const updateConsumableQty = (machineIdx, consIdx, qty) => {
    const updated = [...machineDispatches];
    updated[machineIdx].consumables[consIdx].quantity = Math.max(1, qty);
    setMachineDispatches(updated);
  };

  const removeConsumable = (machineIdx, consIdx) => {
    const updated = [...machineDispatches];
    updated[machineIdx].consumables.splice(consIdx, 1);
    setMachineDispatches(updated);
  };

  const removeMachineBlock = (machineIdx) => {
    setMachineDispatches(prev => prev.filter((_, i) => i !== machineIdx));
  };

  // --- Submit all ---
  const handleSubmitMulti = async (e) => {
    e.preventDefault();
    const totalItems = machineDispatches.reduce((sum, md) => sum + md.consumables.length, 0);
    if (totalItems === 0) {
      toast.error('Προσθέστε τουλάχιστον ένα αναλώσιμο');
      return;
    }
    try {
      for (const block of machineDispatches) {
        for (const item of block.consumables) {
          await dispatchesAPI.create({
            machine_id: block.machineId,
            consumable_id: item.consumable_id,
            dispatch_date: dispatchDate,
            dispatch_note: dispatchNote,
            quantity: item.quantity,
            notes: notes
          });
        }
      }
      toast.success(`${totalItems} αποστολ${totalItems > 1 ? 'ές' : 'ή'} καταχωρήθηκαν!`);
      fetchData();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα αποθήκευσης');
    }
  };

  // --- Edit mode ---
  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    try {
      await dispatchesAPI.update(editingDispatch.id, formData);
      toast.success('Η αποστολή ενημερώθηκε!');
      fetchData();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα αποθήκευσης');
    }
  };

  const handleEdit = (dispatch) => {
    setPendingAction({ type: 'edit', dispatch });
    setShowPasswordModal(true);
  };

  const handleDelete = (dispatch) => {
    setPendingAction({ type: 'delete', dispatch });
    setShowPasswordModal(true);
  };

  const handlePasswordConfirm = async (password) => {
    const isValid = await verifyAdminPassword(password);
    if (isValid && pendingAction) {
      setShowPasswordModal(false);
      if (pendingAction.type === 'edit') {
        setEditingDispatch(pendingAction.dispatch);
        setFormData(pendingAction.dispatch);
        const cons = await fetchMachineConsumables(pendingAction.dispatch.machine_id);
        setEditConsumables(cons);
        setShowModal(true);
      } else if (pendingAction.type === 'delete') {
        try {
          await dispatchesAPI.delete(pendingAction.dispatch.id);
          toast.success('Η αποστολή διαγράφηκε!');
          fetchData();
        } catch { toast.error('Σφάλμα διαγραφής'); }
      }
      setPendingAction(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDispatch(null);
    setSelectedCustomerId('');
    setCustomerMachines([]);
    setMachineDispatches([]);
    setDispatchDate(new Date().toISOString().split('T')[0]);
    setDispatchNote('');
    setNotes('');
    setCustomerSearch('');
    setEditConsumables([]);
    setFormData({
      machine_id: '', consumable_id: '',
      dispatch_date: new Date().toISOString().split('T')[0],
      dispatch_note: '', quantity: 1, notes: ''
    });
  };

  // --- Table data ---
  const enrichedDispatches = dispatches.map(d => {
    const machine = machines.find(m => m.id === d.machine_id);
    return {
      ...d,
      machine_info: machine ? `${machine.brand} ${machine.model}` : 'N/A',
      machine_serial: machine?.machine_serial || '',
      customer_name: machine?.company_name || 'N/A',
      consumable_name: d.consumable_name || 'N/A'
    };
  });

  const columns = [
    { key: 'dispatch_date', label: 'Ημ/νία', render: (item) => new Date(item.dispatch_date).toLocaleDateString('el-GR') },
    { key: 'customer_name', label: 'Πελάτης' },
    { key: 'machine_serial', label: 'S/N' },
    { key: 'machine_info', label: 'Μηχάνημα' },
    { key: 'consumable_name', label: 'Αναλώσιμο' },
    { key: 'quantity', label: 'Ποσότητα' },
    { key: 'dispatch_note', label: 'Δελτίο' },
  ];

  // Filter customers by search
  const filteredCustomers = customerSearch
    ? customers.filter(c => normalizeGreek(c.company_name || '').includes(normalizeGreek(customerSearch)))
    : customers;

  if (loading) {
    return <div className="flex justify-center py-12">Φόρτωση...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Αποστολές Αναλωσίμων</h1>
          <p className="text-gray-600">Διαχείριση αποστολών</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#004a99] hover:bg-[#003a7a]" data-testid="new-dispatch-btn">
          <Plus className="h-4 w-4 mr-2" />
          Νέα Αποστολή
        </Button>
      </div>

      <DataTable
        data={enrichedDispatches}
        columns={columns}
        groupByKey="machine_serial"
        onEdit={handleEdit}
        onDelete={handleDelete}
        searchPlaceholder="Αναζήτηση αποστολών..."
      />

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in-0 zoom-in-95 duration-300">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{editingDispatch ? 'Επεξεργασία Αποστολής' : 'Νέα Αποστολή'}</CardTitle>
                <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {editingDispatch ? (
                /* ========== EDIT MODE ========== */
                <form onSubmit={handleSubmitEdit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Μηχάνημα *</label>
                    <select
                      value={formData.machine_id}
                      onChange={async (e) => {
                        const mid = Number(e.target.value);
                        setFormData({...formData, machine_id: mid, consumable_id: ''});
                        const cons = await fetchMachineConsumables(mid);
                        setEditConsumables(cons);
                      }}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Επιλέξτε μηχάνημα</option>
                      {machines.map(m => (
                        <option key={m.id} value={m.id}>
                          {m.company_name} - {m.brand} {m.model} ({m.machine_serial})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Αναλώσιμο *</label>
                    <select
                      value={formData.consumable_id}
                      onChange={(e) => setFormData({...formData, consumable_id: Number(e.target.value)})}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Επιλέξτε αναλώσιμο</option>
                      {editConsumables.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Ημερομηνία *</label>
                      <Input type="date" value={formData.dispatch_date} onChange={(e) => setFormData({...formData, dispatch_date: e.target.value})} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Ποσότητα</label>
                      <Input type="number" value={formData.quantity} onChange={(e) => setFormData({...formData, quantity: Number(e.target.value)})} min="1" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Δελτίο Αποστολής</label>
                    <Input value={formData.dispatch_note} onChange={(e) => setFormData({...formData, dispatch_note: e.target.value})} placeholder="π.χ. ΔΑ-12345" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Σημειώσεις</label>
                    <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-md" rows="2" />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={handleCloseModal}>Άκυρο</Button>
                    <Button type="submit" className="bg-[#004a99] hover:bg-[#003a7a]">
                      <Save className="h-4 w-4 mr-2" /> Αποθήκευση
                    </Button>
                  </div>
                </form>
              ) : (
                /* ========== CREATE MODE: Customer -> Machines -> Consumables ========== */
                <form onSubmit={handleSubmitMulti} className="space-y-4">

                  {/* STEP 1: Customer */}
                  <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
                    <label className="text-sm font-semibold text-blue-800 flex items-center gap-1 mb-2">
                      <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">1</span>
                      Επιλέξτε Πελάτη *
                    </label>
                    <Input
                      placeholder="Αναζήτηση πελάτη..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="mb-2"
                      data-testid="customer-search-input"
                    />
                    <select
                      value={selectedCustomerId}
                      onChange={(e) => handleCustomerSelect(e.target.value)}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white"
                      required
                      data-testid="customer-select"
                    >
                      <option value="">-- Επιλέξτε πελάτη --</option>
                      {filteredCustomers.map(c => (
                        <option key={c.id} value={c.id}>{c.company_name}</option>
                      ))}
                    </select>
                    {selectedCustomerId && (
                      <p className="text-xs text-blue-600 mt-1">
                        {customerMachines.length} μηχάνημα{customerMachines.length !== 1 ? 'τα' : ''} βρέθηκαν
                      </p>
                    )}
                  </div>

                  {/* Machine Blocks: Each has its own machine selector + consumables */}
                  {selectedCustomerId && machineDispatches.map((block, machineIdx) => {
                    const usedMachineIds = machineDispatches.filter((_, i) => i !== machineIdx).map(md => md.machineId).filter(Boolean);
                    const selectableMachines = customerMachines.filter(m => !usedMachineIds.includes(m.id));
                    const remainingCons = block.availableConsumables.filter(
                      c => !block.consumables.find(ci => ci.consumable_id === c.id)
                    );
                    const blockLabel = String.fromCharCode(65 + machineIdx); // A, B, C...

                    return (
                      <div key={machineIdx} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                        {/* Block header */}
                        <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b">
                          <span className="font-bold text-sm text-gray-700 flex items-center gap-2">
                            <span className="bg-[#004a99] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">{blockLabel}</span>
                            {block.machineId ? block.machineName : 'Επιλογή Μηχανήματος'}
                          </span>
                          {machineDispatches.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700"
                              onClick={() => removeMachineBlock(machineIdx)} data-testid={`remove-machine-${machineIdx}`}>
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>

                        <div className="p-4 space-y-3">
                          {/* Machine selector inside block */}
                          {!block.machineId ? (
                            <div>
                              <label className="text-sm font-medium flex items-center gap-1 mb-1">
                                <Printer className="h-4 w-4 text-blue-600" />
                                Μηχάνημα *
                              </label>
                              <select
                                value=""
                                onChange={(e) => handleSelectMachineInBlock(machineIdx, e.target.value)}
                                className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white"
                                data-testid={`machine-select-${machineIdx}`}
                              >
                                <option value="">-- Επιλέξτε μηχάνημα --</option>
                                {selectableMachines.map(m => (
                                  <option key={m.id} value={m.id}>
                                    {m.brand} {m.model} ({m.machine_serial})
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <>
                              {/* Added consumables */}
                              {block.consumables.map((item, consIdx) => (
                                <div key={consIdx} className="flex items-center gap-2 bg-white p-2 rounded border">
                                  <span className="flex-1 text-sm font-medium">{item.name}</span>
                                  <Input
                                    type="number" value={item.quantity}
                                    onChange={(e) => updateConsumableQty(machineIdx, consIdx, Number(e.target.value))}
                                    min="1" className="w-20 h-8 text-center"
                                  />
                                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700"
                                    onClick={() => removeConsumable(machineIdx, consIdx)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}

                              {/* Add consumable selector */}
                              {remainingCons.length === 0 && block.consumables.length === 0 ? (
                                <p className="text-sm text-orange-600">Δεν βρέθηκαν αναλώσιμα για αυτό το μοντέλο.</p>
                              ) : remainingCons.length === 0 ? (
                                <p className="text-sm text-green-600">Όλα τα αναλώσιμα προστέθηκαν.</p>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Package className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                  <select
                                    value="" onChange={(e) => addConsumableToMachine(machineIdx, e.target.value)}
                                    className="flex-1 h-9 px-3 border border-gray-300 rounded-md bg-white text-sm"
                                    data-testid={`add-consumable-${machineIdx}`}
                                  >
                                    <option value="">+ Προσθήκη αναλωσίμου</option>
                                    {remainingCons.map(c => (
                                      <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Add another machine button */}
                  {selectedCustomerId && customerMachines.length > 0 && (
                    (() => {
                      const usedIds = machineDispatches.map(md => md.machineId).filter(Boolean);
                      const hasUnselected = machineDispatches.some(md => !md.machineId);
                      const moreAvailable = customerMachines.filter(m => !usedIds.includes(m.id)).length > 0;
                      if (!hasUnselected && moreAvailable) {
                        return (
                          <Button type="button" variant="outline" className="w-full border-dashed border-2 text-gray-600 hover:text-blue-700 hover:border-blue-400"
                            onClick={handleAddMachineBlock} data-testid="add-machine-block-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            + Προσθήκη Μηχανήματος
                          </Button>
                        );
                      }
                      return null;
                    })()
                  )}

                  {/* Common fields - show when at least one machine is selected */}
                  {machineDispatches.some(md => md.machineId) && (
                    <>
                      <div className="border-t pt-4 mt-2">
                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-1 mb-3">
                          <span className="bg-gray-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">3</span>
                          Στοιχεία Αποστολής
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">Ημερομηνία *</label>
                            <Input type="date" value={dispatchDate} onChange={(e) => setDispatchDate(e.target.value)} required data-testid="dispatch-date" />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Δελτίο Αποστολής</label>
                            <Input value={dispatchNote} onChange={(e) => setDispatchNote(e.target.value)} placeholder="π.χ. ΔΑ-12345" data-testid="dispatch-note" />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Σημειώσεις</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md" rows="2" data-testid="dispatch-notes" />
                      </div>
                    </>
                  )}

                  {/* Submit */}
                  <div className="flex gap-3 justify-end pt-2">
                    <Button type="button" variant="outline" onClick={handleCloseModal}>Άκυρο</Button>
                    <Button type="submit" className="bg-[#004a99] hover:bg-[#003a7a]"
                      disabled={machineDispatches.reduce((sum, md) => sum + md.consumables.length, 0) === 0}
                      data-testid="submit-dispatch-btn"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Αποστολή ({machineDispatches.reduce((sum, md) => sum + md.consumables.length, 0)} είδ{machineDispatches.reduce((sum, md) => sum + md.consumables.length, 0) === 1 ? 'ος' : 'η'})
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => { setShowPasswordModal(false); setPendingAction(null); }}
        onConfirm={handlePasswordConfirm}
        title={pendingAction?.type === 'delete' ? 'Επιβεβαίωση Διαγραφής' : 'Επιβεβαίωση Επεξεργασίας'}
      />
    </div>
  );
};

export default DispatchesPage;
